import React, { useCallback, useState } from 'react';
import { Eye, Loader2, AlertCircle } from 'lucide-react';
import {
  LabelLayout,
  LabelElement,
  LabelElementType,
  LabelPlaceholder,
  TableSchema,
  LabelContext,
  FALLBACK_PLACEHOLDERS,
  createEmptyLayout,
  createElement,
  generateSampleRows
} from '../../../../../types/labelDesign';
import { ElementToolbox } from './ElementToolbox';
import { LabelCanvas } from './LabelCanvas';
import { PropertyPanel } from './PropertyPanel';
import { SampleDataDialog } from './SampleDataDialog';
import { AlignToolbar } from './AlignToolbar';
import reportConfigurationService from '../../../../../services/reportConfigurationService';
import labelPlaceholderService from '../../../../../services/labelPlaceholderService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface LabelDesignerProps {
  /** Start-layout. Hvis null/undefined, opprettes en tom layout. */
  initialLayout: LabelLayout | null;
  /** Etikettens fysiske dimensjoner (fra Reports-tabellen). */
  widthMm: number;
  heightMm: number;
  /** ReportType brukes til å filtrere hvilke plassholdere som er relevante. */
  reportType?: number;
  onChange: (layout: LabelLayout) => void;
}

export const LabelDesigner: React.FC<LabelDesignerProps> = ({
  initialLayout,
  widthMm,
  heightMm,
  reportType,
  onChange
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo;

  const [layout, setLayout] = useState<LabelLayout>(() => {
    if (initialLayout) {
      return { ...initialLayout, widthMm, heightMm };
    }
    return createEmptyLayout(widthMm, heightMm);
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [zoom, setZoom] = useState<number>(4);
  const [lockAspectRatio, setLockAspectRatio] = useState<boolean>(false);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showSampleDialog, setShowSampleDialog] = useState(false);
  const [placeholders, setPlaceholders] = useState<LabelPlaceholder[]>(FALLBACK_PLACEHOLDERS);
  const [tableSchemas, setTableSchemas] = useState<TableSchema[]>([]);
  const [sampleData, setSampleData] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    FALLBACK_PLACEHOLDERS.forEach((p) => { defaults[p.key] = p.exampleValue; });
    return defaults;
  });

  // Hent plassholdere fra backend når komponenten mounter eller reportType endres
  React.useEffect(() => {
    if (!companyNo) return;
    let cancelled = false;

    labelPlaceholderService
      .getPlaceholders(companyNo, reportType)
      .then((list) => {
        if (cancelled) return;
        setPlaceholders(list);
        // Oppdater sampleData med default-verdier for nye nøkler
        setSampleData((prev) => {
          const merged = { ...prev };
          list.forEach((p) => {
            if (merged[p.key] === undefined) merged[p.key] = p.exampleValue;
          });
          return merged;
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Kunne ikke hente plassholder-katalog, bruker fallback:', err);
        }
      });

    // Hent tabellskjema (kolonne-kataloger for Items/Orders/OrderLine/POrders/POrderLine/StcBin)
    labelPlaceholderService
      .getTableSchemas(companyNo)
      .then((schemas) => {
        if (!cancelled) setTableSchemas(schemas);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Kunne ikke hente tabellskjema:', err);
        }
      });

    return () => { cancelled = true; };
  }, [companyNo, reportType]);

  // Hold layout i sync med eksterne dimensjoner
  React.useEffect(() => {
    setLayout((prev) => ({ ...prev, widthMm, heightMm }));
  }, [widthMm, heightMm]);

  // Rapporter endringer til parent
  React.useEffect(() => {
    onChange(layout);
  }, [layout, onChange]);

  // PropertyPanel viser kun ÉTT element — uendret oppførsel fra før.
  const selectedElement = selectedIds.length === 1
    ? layout.elements.find((el) => el.id === selectedIds[0]) || null
    : null;
  const selectedElements = layout.elements.filter((el) => selectedIds.includes(el.id));

  const updateLayout = useCallback((updater: (prev: LabelLayout) => LabelLayout) => {
    setLayout(updater);
  }, []);

  // Klikk på element: additive (Shift/Ctrl) veksler medlemskap; ellers eneste valgte.
  // id===null tømmer utvalget (klikk på tom flate).
  const handleSelect = useCallback((id: string | null, additive?: boolean) => {
    if (id === null) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds((prev) => {
      if (!additive) return [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  }, []);

  const handleAddElement = (type: LabelElementType) => {
    // Maks ett Table-element per layout (dokumentmodus har én tabellregion)
    if (type === 'Table' && layout.elements.some((el) => el.type === 'Table')) return;

    const newEl = type === 'Table'
      // Tabellen plasseres i full bredde med rom for header over og footer under
      ? { ...createElement(type, 10, 30), width: Math.max(20, widthMm - 20), height: Math.max(20, heightMm - 60) }
      : createElement(type, widthMm / 2 - 15, heightMm / 2 - 5);
    updateLayout((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedIds([newEl.id]);
  };

  const handleElementChange = (updated: LabelElement) => {
    updateLayout((prev) => ({
      ...prev,
      elements: prev.elements.map((el) => (el.id === updated.id ? updated : el))
    }));
  };

  // Batch-oppdatering av flere elementer samtidig (align/distribute, multi-nudge).
  const handleElementsChange = useCallback((updatedList: LabelElement[]) => {
    if (updatedList.length === 0) return;
    const map = new Map(updatedList.map((e) => [e.id, e]));
    updateLayout((prev) => ({
      ...prev,
      elements: prev.elements.map((el) => map.get(el.id) || el)
    }));
  }, [updateLayout]);

  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    const toDelete = new Set(selectedIds);
    updateLayout((prev) => ({
      ...prev,
      elements: prev.elements.filter((el) => !toDelete.has(el.id))
    }));
    setSelectedIds([]);
  };

  const handleDuplicate = () => {
    if (!selectedElement) return;
    const copy: LabelElement = {
      ...selectedElement,
      id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      x: selectedElement.x + 3,
      y: selectedElement.y + 3
    };
    updateLayout((prev) => ({ ...prev, elements: [...prev.elements, copy] }));
    setSelectedIds([copy.id]);
  };

  // Tastatursnarveier — ignoreres når fokus er i et inntastingsfelt (PropertyPanel).
  React.useEffect(() => {
    const NUDGE_MM = 1;
    const NUDGE_GRID_MM = 5;

    const isEditableTarget = (t: EventTarget | null): boolean => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };

    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      const ctrl = e.ctrlKey || e.metaKey;

      // Ctrl/Cmd+A — velg alle
      if (ctrl && (e.key === 'a' || e.key === 'A')) {
        if (layout.elements.length === 0) return;
        e.preventDefault();
        setSelectedIds(layout.elements.map((el) => el.id));
        return;
      }
      // Ctrl/Cmd+D — dupliser valgte
      if (ctrl && (e.key === 'd' || e.key === 'D')) {
        if (selectedIds.length === 0) return;
        e.preventDefault();
        const sel = layout.elements.filter((el) => selectedIds.includes(el.id));
        const copies = sel.map((el, i) => ({
          ...el,
          id: `el-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          x: el.x + 3,
          y: el.y + 3
        }));
        updateLayout((prev) => ({ ...prev, elements: [...prev.elements, ...copies] }));
        setSelectedIds(copies.map((c) => c.id));
        return;
      }
      // Escape — tøm utvalg
      if (e.key === 'Escape') {
        if (selectedIds.length > 0) setSelectedIds([]);
        return;
      }
      // Delete / Backspace — slett valgte
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.length === 0) return;
        e.preventDefault();
        handleDelete();
        return;
      }
      // Piltaster — nudge valgte
      const arrows: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]
      };
      const dir = arrows[e.key];
      if (dir) {
        if (selectedIds.length === 0) return;
        e.preventDefault();
        const stepMm = e.shiftKey ? NUDGE_GRID_MM : NUDGE_MM;
        const sel = layout.elements.filter((el) => selectedIds.includes(el.id));
        const moved = sel.map((el) => ({
          ...el,
          x: Math.round((el.x + dir[0] * stepMm) * 100) / 100,
          y: Math.round((el.y + dir[1] * stepMm) * 100) / 100
        }));
        handleElementsChange(moved);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, layout.elements, updateLayout, handleElementsChange]);

  const handleGeneratePreview = async (dataToUse: Record<string, string>, context: LabelContext) => {
    if (!companyNo) {
      setPreviewError(t('manager.modules.labelDesigner.preview.missingCompany', {}, 'Mangler firma-kontekst — logg inn på nytt.'));
      return;
    }
    if (layout.elements.length === 0) {
      setPreviewError(t('manager.modules.labelDesigner.preview.noElements', {}, 'Legg til minst ett element på etiketten før forhåndsvisning.'));
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      // Send kun ikke-tomme kontekstfelter så backend ikke slår opp unødvendig
      const hasContext = Object.values(context).some((v) => v !== undefined && v !== '' && v !== null);
      // Eksempel-rader for Table-elementer (dokumentmodus) — med kontekst henter
      // backend heller faktiske rader (f.eks. PListLines fra PListID)
      const sampleRows = hasContext ? undefined : generateSampleRows(layout);
      const blob = await reportConfigurationService.getCustomLabelPreview(
        companyNo,
        layout,
        dataToUse,
        hasContext ? context : undefined,
        sampleRows
      );
      const url = URL.createObjectURL(blob);
      // Åpne PDF i nytt vindu — synlig og enkelt å skrive ut
      const win = window.open(url, '_blank');
      if (!win) {
        setPreviewError(t('manager.modules.labelDesigner.preview.popupBlocked', {}, 'Kunne ikke åpne forhåndsvisning — popup-blokkering? Sjekk nettleserinnstillinger.'));
      }
      // Release objectURL etter kort stund slik at nytt vindu får lastet PDF-en
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setShowSampleDialog(false);
      setSampleData(dataToUse);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('manager.modules.labelDesigner.preview.unknownError', {}, 'Ukjent feil ved forhåndsvisning'));
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[500px]">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center gap-3 text-sm text-[var(--color-text-secondary)]">
          <span>{layout.widthMm} × {layout.heightMm} mm</span>
          <span>·</span>
          <span>{t('manager.modules.labelDesigner.toolbar.elementCount', { '0': layout.elements.length }, `${layout.elements.length} elementer`)}</span>
          {selectedIds.length > 0 && (
            <>
              <span>·</span>
              <span className="text-blue-600">{t('manager.modules.labelDesigner.toolbar.selectedCount', { '0': selectedIds.length }, `${selectedIds.length} valgt`)}</span>
            </>
          )}
          <span className="w-px h-5 bg-[var(--color-border)] mx-1" />
          <AlignToolbar
            selected={selectedElements}
            widthMm={layout.widthMm}
            heightMm={layout.heightMm}
            onApply={handleElementsChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[var(--color-text-secondary)]">{t('manager.modules.labelDesigner.toolbar.zoom', {}, 'Zoom:')}</label>
          <input
            type="range"
            min={2}
            max={8}
            step={0.5}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-24"
          />
          <span className="text-xs text-[var(--color-text-secondary)] w-10">{zoom}×</span>
          <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none ml-2"
            title={t('manager.modules.labelDesigner.toolbar.snapTooltip', {}, 'Snap posisjon og størrelse til 5 mm-rutenettet ved draging og skalering')}>
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(e) => setSnapToGrid(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            {t('manager.modules.labelDesigner.toolbar.snapToGrid', {}, 'Snap til rutenett')}
          </label>
          <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none ml-2"
            title={t('manager.modules.labelDesigner.toolbar.lockAspectTooltip', {}, 'Behold proporsjon ved drag-skalering — bredde og høyde endres samtidig')}>

            <input
              type="checkbox"
              checked={lockAspectRatio}
              onChange={(e) => setLockAspectRatio(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            {t('manager.modules.labelDesigner.toolbar.lockAspect', {}, 'Behold proporsjon')}
          </label>
          <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none ml-2"
            title={t('manager.modules.labelDesigner.toolbar.fitToPageTooltip', {}, 'Sender fit_to_page eksplisitt. PrintNode skalerer alltid PDF-en til valgt papir uansett, så haken har ingen praktisk effekt. Riktig størrelse styres av Bredde × Lengde.')}>
            <input
              type="checkbox"
              checked={layout.fitToPage ?? false}
              onChange={(e) => updateLayout((prev) => ({ ...prev, fitToPage: e.target.checked }))}
              className="w-3.5 h-3.5"
            />
            {t('manager.modules.labelDesigner.toolbar.fitToPage', {}, 'Skaler ved utskrift')}
          </label>
          <button
            type="button"
            onClick={() => setShowSampleDialog(true)}
            disabled={previewLoading}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {t('manager.modules.labelDesigner.toolbar.preview', {}, 'Forhåndsvis')}
          </button>
        </div>
      </div>

      {/* Main layout: toolbox | canvas | properties */}
      <div className="flex flex-1 min-h-0">
        <ElementToolbox
          onAdd={handleAddElement}
          reportType={reportType}
          hasTable={layout.elements.some((el) => el.type === 'Table')}
        />

        <div className="flex-1 overflow-auto p-4 bg-[var(--color-background)]">
          <LabelCanvas
            layout={layout}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onElementChange={handleElementChange}
            pxPerMm={zoom}
            lockAspectRatio={lockAspectRatio}
            snapToGrid={snapToGrid}
          />
        </div>

        <PropertyPanel
          element={selectedElement}
          placeholders={placeholders}
          tableSchemas={tableSchemas}
          reportType={reportType}
          onChange={handleElementChange}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      </div>

      {/* Errors */}
      {previewError && (
        <div className="p-3 bg-red-50 border-t border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">{t('manager.modules.labelDesigner.preview.failed', {}, 'Forhåndsvisning feilet')}</div>
            <div>{previewError}</div>
          </div>
          <button
            type="button"
            onClick={() => setPreviewError(null)}
            className="text-red-600 hover:text-red-800 text-xs"
          >
            {t('common.buttons.close', {}, 'Lukk')}
          </button>
        </div>
      )}

      {/* Sample data dialog */}
      <SampleDataDialog
        isOpen={showSampleDialog}
        placeholders={placeholders}
        extraKeys={extraKeysInLayout(layout, placeholders)}
        contextTables={contextTablesInLayout(layout)}
        tableDataSources={layout.elements
          .filter((el) => el.type === 'Table' && el.dataSource)
          .map((el) => el.dataSource!)}
        initialData={sampleData}
        onCancel={() => setShowSampleDialog(false)}
        onSubmit={handleGeneratePreview}
        loading={previewLoading}
      />
    </div>
  );
};

/**
 * Trekker ut alle plassholder-nøkler brukt i layout (både enkle og dotted)
 * som ikke allerede er i den kjente plassholder-katalogen.
 */
/**
 * Alias-plassholder → resolver-tabell. Standardmalene bruker alias-nøkler
 * ({{ItemNo}}, {{BatchNo}} …) i stedet for prikk-notasjon ({{Items.ItemNo}}),
 * men DB-oppslaget er det samme — resolveren fyller aliasene fra tabellradene.
 * Mappingen styrer hvilke kontekst-inputs forhåndsvisningsdialogen viser.
 */
const ALIAS_TO_TABLE: Record<string, string> = {
  ItemNo: 'Items', ItemName: 'Items', ItemDescription: 'Items', ItemUnitCode: 'Items', GTIN: 'Items',
  BinCode: 'StcBin', LocationName: 'StcBin', ZoneName: 'StcBin',
  OrderNo: 'Orders', LineNo: 'OrderLine', CustomerNo: 'Orders', CustomerName: 'Orders',
  CustomerReference: 'Orders', DeliveryAddress: 'Orders',
  BatchNo: 'Orders', ExpiryDate: 'Orders', BestBeforeDate: 'Orders', // via OrderBatchNo (ordre+linje)
  SSCC: 'OrderSSCC', PalletNo: 'PListSSCC',
  // Dokument/forsendelse (PList-/Shipment-kontekst kjeder til Orders)
  PListId: 'PList', ShipmentNo: 'Shipment', DocumentNo: 'PList', CarrierName: 'Orders', DeliveryMethod: 'Orders'
};

/**
 * Finner hvilke resolver-tabeller layouten refererer — både via prikk-notasjon
 * ({{Items.ItemNo}} → Items) og via alias-plassholdere ({{ItemNo}} → Items).
 * Brukes til å vise relevante DB-kontekst-inputs i forhåndsvisningsdialogen.
 */
function contextTablesInLayout(layout: LabelLayout): string[] {
  const tables = new Set<string>();
  const regex = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)(?:\.([A-Za-z_][A-Za-z0-9_]*))?\s*\}\}/g;
  for (const el of layout.elements) {
    for (const field of [el.text, el.value]) {
      if (!field) continue;
      let m: RegExpExecArray | null;
      regex.lastIndex = 0;
      while ((m = regex.exec(field)) !== null) {
        if (m[2]) tables.add(m[1]); // prikk-notasjon: {{Tabell.Kolonne}}
        else if (ALIAS_TO_TABLE[m[1]]) tables.add(ALIAS_TO_TABLE[m[1]]);
      }
    }
  }
  return Array.from(tables);
}

function extraKeysInLayout(layout: LabelLayout, known: LabelPlaceholder[]): string[] {
  const knownKeys = new Set(known.map((p) => p.key));
  const found = new Set<string>();
  const regex = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)?)\s*\}\}/g;
  for (const el of layout.elements) {
    for (const field of [el.text, el.value]) {
      if (!field) continue;
      let m: RegExpExecArray | null;
      regex.lastIndex = 0;
      while ((m = regex.exec(field)) !== null) {
        const key = m[1];
        if (!knownKeys.has(key)) found.add(key);
      }
    }
  }
  return Array.from(found).sort();
}
